import { useCallback, useEffect, useState } from 'react';
import { useFetcher } from 'react-router';

import type { LoaderResponse } from '@jane/bloom/routes/_menu.products.$productId.$slug/types';
import type { UpvotePayload } from '@jane/bloom/routes/products.$productId.$slug.reviews/types';
import { ReviewIntent } from '@jane/bloom/routes/products.$productId.$slug.reviews/types';
import type { SortOrder } from '@jane/bloom/types/schemas/algolia/review';
import { buildFormData } from '@jane/bloom/utils/formData';
import type { ProductReviewViewModel } from '@jane/bloom/viewModels/productReviews';

import { useBloomBasename } from './useBloomBasename';

type Args = {
  initialReviews: ProductReviewViewModel[];
  productId: number;
  productSlug?: string;
  reviewCount: number;
};

type ReturnType = {
  canLoadMoreReviews: boolean;
  currentSortOrder: SortOrder;
  displayableReviews: ProductReviewViewModel[];
  handleLoadMore: () => void;
  handleSort: (nextOrder: SortOrder) => void;
  isLoading: boolean;
  reviewPaginationSummary: string;
  upvote: (reviewId: string) => void;
};

export function useProductReviewsFetcher({
  initialReviews,
  productId,
  productSlug,
  reviewCount,
}: Args): ReturnType {
  const basename = useBloomBasename();
  const fetcher = useFetcher<LoaderResponse>();
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<SortOrder>('recent');
  const [displayableReviews, setDisplayableReviews] = useState(initialReviews);

  const canLoadMoreReviews = displayableReviews.length < reviewCount;
  const reviewPaginationSummary = `1-${displayableReviews.length} of ${reviewCount} reviews`;

  const handleSort = (newSortOrder: SortOrder) => {
    setSortOrder(newSortOrder);
    const params = new URLSearchParams({
      reviewsPage: `${page}`,
      reviewsSort: newSortOrder,
    });
    return fetcher.load(
      `${basename}products/${productId}/${productSlug}?${params.toString()}`
    );
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);

    const params = new URLSearchParams({
      reviewsPage: `${nextPage}`,
      reviewsSort: sortOrder,
    });
    return fetcher.load(
      `${basename}products/${productId}/${productSlug}?${params.toString()}`
    );
  };

  useEffect(() => {
    const loadReviews = async () => {
      if (fetcher.data && 'productReviews' in fetcher.data) {
        const data = fetcher.data as LoaderResponse;
        const productReviews = await data.productReviews;

        if (productReviews.reviews?.length) {
          setDisplayableReviews(productReviews.reviews);
        }
      }
    };

    loadReviews();
  }, [fetcher.data]);

  const upvote = useCallback(
    (reviewId: string) => {
      const formData: UpvotePayload = {
        page,
        productId,
        reviewId,
        sortOrder,
      };

      fetcher.submit(
        buildFormData({
          intent: ReviewIntent.Upvote,
          ...formData,
        }),
        {
          action: `${basename}products/${productId}/${productSlug}/reviews`,
          method: 'post',
        }
      );
    },
    [basename, fetcher, page, sortOrder, productSlug, productId]
  );

  return {
    canLoadMoreReviews,
    currentSortOrder: sortOrder,
    displayableReviews,
    handleLoadMore,
    handleSort,
    isLoading: fetcher.state !== 'idle',
    reviewPaginationSummary,
    upvote,
  };
}
